/**
 * Page transitions through the View Transitions API.
 *
 * The browser keeps a snapshot of the old screen on top until the new route
 * has rendered, then animates between the two. Nothing in between is ever
 * painted, so there's no blank frame, no footer showing while a page mounts,
 * and no glimpse of the old page as the menu closes.
 *
 * The animations live in App.vue (::view-transition-*). Browsers without the
 * API, and anyone with reduced motion on, keep the plain fade in App.vue.
 */
import { nextTick, ref } from 'vue';

// True while a view transition owns the screen. App.vue turns its own page
// transition off then (:css="false"), so the outgoing page leaves in the same
// render and isn't caught in the new snapshot.
export const viewTransitionActive = ref(false);

const supported = () =>
  typeof document !== 'undefined' &&
  'startViewTransition' in document &&
  !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function installViewTransitions(router) {
  let finishRender = null;

  router.beforeResolve((to, from) => {
    // Same page (a hash or query change) and the first load don't animate.
    if (!supported() || !from.matched.length || to.path === from.path) return;

    const root = document.documentElement;
    return new Promise((resolveNavigation) => {
      root.classList.add('vt-active');
      viewTransitionActive.value = true;
      const transition = document.startViewTransition(
        () =>
          new Promise((rendered) => {
            finishRender = rendered;
            resolveNavigation(); // the old screen is captured; let the route change
          })
      );
      transition.finished.finally(() => {
        root.classList.remove('vt-active', 'vt-from-menu');
        viewTransitionActive.value = false;
      });
    });
  });

  router.afterEach(async () => {
    if (!finishRender) return;
    await nextTick(); // the new route, and the closed menu, are in the DOM
    finishRender();
    finishRender = null;
  });

  // A failed navigation must not leave the screen frozen on the snapshot.
  router.onError(() => {
    finishRender?.();
    finishRender = null;
  });
}
